import {
  EnergyFlow,
  EnergyType,
  PrimaryEnergyFlow,
  Quadrant,
  WasteType,
} from './EnergyFlow'
import type { ProviderProperty } from '../provider/provider-properties'
import { SystemContainerCalculator } from './SystemContainerCalculator'
import { reduceToSum, reduceToSumOfItems } from '../util/array-utility'
import { Provider } from './providers/common/Provider'
import { HybridHeatPumpExtraOutputField } from './providers/HybridHeatPumpProvider'
import { EnergyBusCalculator } from './EnergyBusCalculator'
import {
  getValuesFromDemands,
  getValuesFromProviders,
} from './calculator-values-utility'
import { EnergyBusResultKey } from '../provider/result-categories'

export type ResultFilter = {
  quadrant?: Quadrant
  energyType?: EnergyType
  wasteType?: WasteType
  providerProperty?: ProviderProperty

  /** If true, use initial demands instead of calculated flows */
  isDemandBased?: boolean

  /** If true, do not filter out transferred demand ids */
  includeTransferredDemands?: boolean

  /** If set, get values from hybrid heat pump extra outputs */
  hybridHeatPumpExtraOutputKey?: HybridHeatPumpExtraOutputField

  /** If set, add relevant energy bus values to result */
  energyBusResultKey?: EnergyBusResultKey

  /** If set, provider flows are filtered accordingly */
  additionalFlowFilter?: (flow: EnergyFlow) => boolean
}

export type FilteredResult = {
  values: number[]
  total: number
  max: number
  isEmpty: boolean
}

export const convertValuesToResult = (values: number[]): FilteredResult => ({
  values,
  total: values.reduce(reduceToSum, 0),
  max: Math.max(...values, 0),
  isEmpty: values.length === 0,
})

export function getFilteredResultsFromDemands(
  demands: PrimaryEnergyFlow[],
  filter: ResultFilter,
): FilteredResult {
  const values = getValuesFromDemands(demands, filter)

  return convertValuesToResult(values)
}

export function getFilteredResultsFromProviders(
  providers: Provider[],
  filter: ResultFilter,
): FilteredResult {
  const values = getValuesFromProviders(providers, filter)

  return convertValuesToResult(values)
}

export function getFilteredResultsFromEnergyBus(
  energyBus: EnergyBusCalculator,
  key: EnergyBusResultKey,
): FilteredResult {
  const values = energyBus.getResultValues()[key]

  return convertValuesToResult(values)
}

export function getFilteredResultsFromContainers(
  filter: ResultFilter,
  containers: SystemContainerCalculator[],
  energyBus?: EnergyBusCalculator | null,
): FilteredResult {
  const resultFromContainers = filter.isDemandBased
    ? getFilteredResultsFromDemands(
        containers.flatMap((container) => container.getPrimaryDemands()),
        filter,
      )
    : getFilteredResultsFromProviders(
        containers.flatMap((container) => container.getProviders()),
        filter,
      )

  if (energyBus && filter.energyBusResultKey) {
    const energyBusResultValues =
      energyBus.getResultValues()[filter.energyBusResultKey]

    const totalValues = [
      resultFromContainers.values,
      energyBusResultValues,
    ].reduce(reduceToSumOfItems, [])

    return convertValuesToResult(totalValues)
  }

  return resultFromContainers
}
